import React, { useState } from 'react';

import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, Card, Dialog, H1, ListRow, Separator, Text, Tooltip } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { HouseholdDocument } from './graphql/query.household.generated';

import { BillingPlanDisplayFragment } from '../../../graphql/fragments/billing_plan_display.generated';
import { PaymentType, PlatformFeePaidBy } from '../../../graphql/schema';
import urlBuilder from '../../../Shared/url-builder';
import { BillingRateType, PaymentTypeMap, TIERED_CALCULATION_LABEL } from '../../ClientBilling/BillingPlans/data';
import ViewTieredRateModal from '../../ClientBilling/BillingPlans/view-tiered-rate-modal/ViewTieredRateModal';

const FeeRate: React.FC<{
  plan: BillingPlanDisplayFragment;
  platformFee: number;
  isTieredBillingModalOpen: boolean;
  setIsTieredBillingModalOpen: (open: boolean) => void;
}> = ({ plan, platformFee, isTieredBillingModalOpen, setIsTieredBillingModalOpen }) => {
  const { billingRate } = plan;
  switch (billingRate.__typename) {
    case BillingRateType.AssetBased:
      return (
        <Box>
          <Text variant="text100" color="text.weak" as="span">
            {billingRate.feeBps} bps
          </Text>
        </Box>
      );
    case BillingRateType.Fixed:
      return (
        <Box>
          <Text variant="text100" color="text.weak" as="span">
            {formatMoney(billingRate.feeCents)}
          </Text>
        </Box>
      );
    case BillingRateType.Tiered:
      return (
        <Box css={{ display: 'flex', alignItems: 'center' }}>
          <Dialog open={isTieredBillingModalOpen} onOpenChange={setIsTieredBillingModalOpen} overlay="default">
            <Dialog.Trigger asChild={true}>
              <Button variant="textPrimary">Tiered</Button>
            </Dialog.Trigger>
            <ViewTieredRateModal
              plan={plan}
              platformFee={platformFee}
              onClose={() => setIsTieredBillingModalOpen(false)}
            />
          </Dialog>
          <Text variant="text100" color="text.weak" as="span" css={{ ml: 'space.50' }}>
            ({TIERED_CALCULATION_LABEL[billingRate.calculationMethod]})
          </Text>
        </Box>
      );
    default:
      return 'N/A';
  }
};

const PlatformFeeText: React.FC<{
  plan: BillingPlanDisplayFragment;
  platformFee: number;
}> = ({ plan, platformFee }) => {
  const { platformFeeAllocation } = plan;
  if (platformFeeAllocation.__typename === 'SharedPlatformFeeAllocation') {
    const { clientPortion } = platformFeeAllocation;
    return (
      <Box>
        <Text variant="text100" color="text.weak" as="span">
          Shared (Your firm {Math.max(platformFee - clientPortion, 0)} bps, client {clientPortion} bps)
        </Text>
      </Box>
    );
  }

  switch (platformFeeAllocation.paidBy) {
    case PlatformFeePaidBy.ClientOnly:
      return (
        <Text variant="text100" color="text.weak" as="span">
          Client {platformFee} bps
        </Text>
      );
    case PlatformFeePaidBy.AdvisorOnly:
      return (
        <Text variant="text100" color="text.weak" as="span">
          Your firm {platformFee} bps
        </Text>
      );
    default:
      return (
        <Text variant="text100" color="text.weak" as="span">
          N/A
        </Text>
      );
  }
};

export const BillingPlanCard: React.FC = () => {
  const householdId = useRequiredParam('householdId');
  const { data } = useGraphQLQuery(HouseholdDocument, {
    variables: { id: householdId },
    throwOnError: true,
  });
  const [isTieredBillingModalOpen, setIsTieredBillingModalOpen] = useState<boolean>(false);

  if (!data) {
    return <CardLoader />;
  }

  if (data.household.__typename === 'RecordNotFoundResult') {
    return null;
  }

  const { billingPlan } = data.household;
  const { platformFee } = data.firm;

  const cardTooltip = (showCashReserveTooltip: boolean) => (
    <Tooltip>
      <Tooltip.Trigger asChild={true} data-testid="platformFeeTooltip">
        <Tooltip.IconButton name="InfoCircle" css={{ ml: 'space.50', color: 'icon.weak' }} />
      </Tooltip.Trigger>
      <Tooltip.Content align="center" side="top">
        {showCashReserveTooltip ? (
          <Text>
            If the full amount isn't available, we'll bill in the following order: first from Cash Reserve accounts,
            starting with the largest balance, then from taxable accounts in order of largest balance, and finally from
            tax-advantaged accounts where the fees were accrued.
          </Text>
        ) : (
          <Text>
            Balances in 401(k) and Cash Reserve accounts are not eligible for advisor fees. Standard 401(k) and Cash
            Reserve balances will not count towards overall household balances for tier determination in Tiered Billing
            Plans.
          </Text>
        )}
      </Tooltip.Content>
    </Tooltip>
  );

  return (
    <Card css={{ height: '100%' }}>
      <Card.Header
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          p: 'space.300',
        }}
      >
        <H1 variant="title200">Billing plan</H1>
        <SambaAutoLink variant="textPrimary" to={urlBuilder.getHouseholdBillingPlanEditPage.absolute({ householdId })}>
          Switch plan
        </SambaAutoLink>
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content
        css={{
          display: 'flex',
          gap: 'space.100',
          flexDirection: 'column',
          mt: 'space.200',
          pb: 'space.300',
        }}
      >
        <ListRow css={{ py: '0', pb: 'space.50' }}>
          <ListRow.LeadingContent>
            <Text as="span" variant="text100">
              {billingPlan.name}
            </Text>
          </ListRow.LeadingContent>
        </ListRow>
        <ListRow css={{ py: '0' }}>
          <ListRow.LeadingContent>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Text as="span" color="text.weak" variant="text100">
                Advisory fee
              </Text>
              {cardTooltip(false)}
            </Box>
          </ListRow.LeadingContent>
          <ListRow.TrailingContent>
            <FeeRate
              plan={billingPlan}
              platformFee={platformFee}
              isTieredBillingModalOpen={isTieredBillingModalOpen}
              setIsTieredBillingModalOpen={setIsTieredBillingModalOpen}
            />
          </ListRow.TrailingContent>
        </ListRow>
        <ListRow css={{ py: '0' }}>
          <ListRow.LeadingContent>
            <Text as="span" color="text.weak" variant="text100">
              Platform fee
            </Text>
          </ListRow.LeadingContent>
          <ListRow.TrailingContent>
            <PlatformFeeText plan={billingPlan} platformFee={platformFee} />
          </ListRow.TrailingContent>
        </ListRow>
        <ListRow css={{ py: '0' }}>
          <ListRow.LeadingContent>
            <Text as="span" color="text.weak" variant="text100">
              Billing from
            </Text>
          </ListRow.LeadingContent>
          <ListRow.TrailingContent>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Text as="span" color="text.weak" variant="text100">
                {PaymentTypeMap[billingPlan.paymentType]}
              </Text>
              {billingPlan.paymentType === PaymentType.CashReserveFirst ? cardTooltip(true) : null}
            </Box>
          </ListRow.TrailingContent>
        </ListRow>
      </Card.Content>
    </Card>
  );
};
